import type {
  PortalBuybackDto,
  PortalBuybackSummaryDto,
  PortalCustomerDto,
  PortalDashboardDto,
  PortalIdentityDto,
  PortalInvoicePrintDto,
  PortalListDto,
  PortalLoginRequest,
  PortalOutstandingDto,
  PortalPaymentDto,
  PortalQuotationDto,
  PortalQuotationPrintDto,
  PortalQuotationSummaryDto,
  PortalReceiptPrintDto,
  PortalRentalDto,
  PortalRentalSummaryDto,
  PortalSaleDto,
  PortalSaleSummaryDto,
} from '@erp-boilerplate/types';

import { api, type QueryParams } from '@/lib/api-client';

/**
 * The customer portal API surface (Phase 6).
 *
 * Every path is under `/portal`, and every one is a GET apart from the three
 * auth calls. That is not an omission: the portal is read-only this phase, so
 * there is no create, update or action method here to be wired up by mistake.
 *
 * Nothing in this file sends a customer id. The server resolves it from the
 * session, which is why a list call takes only a page number.
 */

export const portalAuthService = {
  login: (payload: PortalLoginRequest) =>
    api.post<{ customer: PortalIdentityDto; tokens: { accessToken: string; expiresIn: number } }>(
      '/portal/auth/login',
      payload,
    ),
  /** Rotates the session from the httpOnly portal cookie. */
  refresh: () =>
    api.post<{ customer: PortalIdentityDto; tokens: { accessToken: string; expiresIn: number } }>(
      '/portal/auth/refresh',
      {},
    ),
  logout: () => api.post<void>('/portal/auth/logout', {}),
  me: () => api.get<PortalIdentityDto>('/portal/me'),
};

export const portalService = {
  dashboard: () => api.get<PortalDashboardDto>('/portal/dashboard'),
  profile: () => api.get<PortalCustomerDto>('/portal/profile'),
  outstanding: () => api.get<PortalOutstandingDto>('/portal/outstanding'),

  quotations: (params: QueryParams) =>
    api.get<PortalListDto<PortalQuotationSummaryDto>>('/portal/quotations', params),
  quotation: (id: string) => api.get<PortalQuotationDto>(`/portal/quotations/${id}`),
  quotationDocument: (id: string) =>
    api.get<PortalQuotationPrintDto>(`/portal/quotations/${id}/document`),

  sales: (params: QueryParams) =>
    api.get<PortalListDto<PortalSaleSummaryDto>>('/portal/sales', params),
  sale: (id: string) => api.get<PortalSaleDto>(`/portal/sales/${id}`),
  invoice: (id: string) => api.get<PortalInvoicePrintDto>(`/portal/sales/${id}/invoice`),

  payments: (params: QueryParams) =>
    api.get<PortalListDto<PortalPaymentDto>>('/portal/payments', params),
  payment: (id: string) => api.get<PortalPaymentDto>(`/portal/payments/${id}`),
  receipt: (id: string) => api.get<PortalReceiptPrintDto>(`/portal/payments/${id}/receipt`),

  rentals: (params: QueryParams) =>
    api.get<PortalListDto<PortalRentalSummaryDto>>('/portal/rentals', params),
  rental: (id: string) => api.get<PortalRentalDto>(`/portal/rentals/${id}`),

  buybacks: (params: QueryParams) =>
    api.get<PortalListDto<PortalBuybackSummaryDto>>('/portal/buybacks', params),
  buyback: (id: string) => api.get<PortalBuybackDto>(`/portal/buybacks/${id}`),
};
